import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';
import { formatDateTime12hIST } from '../utils/dateDisplay';

function maskAccountNumber(accountNumber) {
  const s = String(accountNumber || '').replace(/\s/g, '');
  if (s.length > 4) return `****${s.slice(-4)}`;
  return '****';
}

function PlusIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );
}

function BankAccountCard({ item }) {
  return (
    <article className="bank-acct-card">
      <div className="bank-acct-card-head">
        <div className="bank-acct-card-head-text">
          <p className="bank-acct-holder">{item.accountHolderName || '—'}</p>
          <p className="bank-acct-bank-name">{(item.bankName || '').toUpperCase()}</p>
        </div>
        {item.isPrimary ? (
          <span className="bank-acct-badge">Primary</span>
        ) : null}
      </div>
      <div className="bank-acct-details">
        <div className="bank-acct-row">
          <span className="bank-acct-label">Account Number</span>
          <span className="bank-acct-value">{maskAccountNumber(item.accountNumber)}</span>
        </div>
        <div className="bank-acct-row">
          <span className="bank-acct-label">IFSC Code</span>
          <span className="bank-acct-value">{item.ifscCode || '—'}</span>
        </div>
      </div>
      {item.linkedAt ? (
        <p className="bank-acct-meta">{formatDateTime12hIST(item.linkedAt)}</p>
      ) : null}
    </article>
  );
}

const emptyVerify = {
  account_holder_name: '',
  account_number: '',
  ifsc_code: ''
};

function BankAccountsPage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addModalStep, setAddModalStep] = useState('form');
  const [verify, setVerify] = useState(emptyVerify);
  const [pendingLink, setPendingLink] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ kind: '', text: '' });
  const [modalError, setModalError] = useState('');
  /** After closing the sheet, ignore FAB opens briefly so the same tap does not reopen (ghost click). */
  const suppressFabOpenUntilRef = useRef(0);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await webApi.getBankAccounts({ signal });
      if (signal?.aborted) return;
      setList(data?.bankAccounts || []);
      setBanner({ kind: '', text: '' });
    } catch (err) {
      if (signal?.aborted || isCanceledRequest(err)) return;
      setBanner({ kind: 'error', text: err.message || 'Failed to load bank accounts' });
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const openAdd = () => {
    if (Date.now() < suppressFabOpenUntilRef.current) return;
    setVerify(emptyVerify);
    setModalError('');
    setPendingLink(null);
    setAddModalStep('form');
    setAddOpen(true);
  };

  const closeAddModal = () => {
    if (submitting) return;
    suppressFabOpenUntilRef.current = Date.now() + 500;
    setAddOpen(false);
    setAddModalStep('form');
    setPendingLink(null);
    setVerify(emptyVerify);
    setModalError('');
  };

  const runVerifyOnly = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setModalError('');
    try {
      const verifyData = await webApi.verifyBankAccount({
        account_holder_name: verify.account_holder_name.trim(),
        account_number: verify.account_number.trim(),
        ifsc_code: verify.ifsc_code.trim().toUpperCase()
      });
      const bankId = verifyData?.bankId;
      if (!bankId) {
        throw new Error('Verification succeeded but no bank reference was returned');
      }
      setPendingLink({
        bankId: Number(bankId),
        bankVerifiedName: verifyData?.bankVerifiedName || verify.account_holder_name.trim(),
        bankName: verifyData?.bankName
      });
      setAddModalStep('confirm');
    } catch (err) {
      setModalError(err.message || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmLink = async () => {
    if (!pendingLink?.bankId) return;
    setSubmitting(true);
    setModalError('');
    try {
      await webApi.linkBankAccount({ bank_id: pendingLink.bankId, is_primary: true });
      closeAddModal();
      setBanner({ kind: 'success', text: 'Bank account added successfully' });
      await load();
    } catch (err) {
      setModalError(err.message || 'Could not link bank account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bank-acct-page">
      <header className="bank-acct-topbar">
        <button type="button" className="bank-acct-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="bank-acct-topbar-title">Bank Accounts</h1>
        <span className="bank-acct-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="bank-acct-body">
        {banner.text ? (
          <p className={`bank-acct-banner bank-acct-banner--${banner.kind}`} role="status">
            {banner.text}
          </p>
        ) : null}

        {loading ? (
          <p className="bank-acct-loading">Loading…</p>
        ) : list.length === 0 ? (
          <div className="bank-acct-empty">
            <p className="bank-acct-empty-title">No bank accounts</p>
            <p className="bank-acct-empty-sub">Tap + to verify and link an account for payouts.</p>
          </div>
        ) : (
          <div className="bank-acct-list">
            {list.map((item) => (
              <BankAccountCard key={`${item.id}-${item.ifscCode}`} item={item} />
            ))}
          </div>
        )}
      </div>

      <button type="button" className="bank-acct-fab" onClick={openAdd} aria-label="Add bank account">
        <PlusIcon />
      </button>

      {addOpen ? (
        <div
          className={`bank-acct-modal-overlay${addModalStep === 'confirm' ? ' bank-acct-modal-overlay--confirm' : ''}`}
          role="presentation"
          onClick={() => !submitting && closeAddModal()}
        >
          <div
            className={`bank-acct-modal ${addModalStep === 'confirm' ? 'bank-acct-modal--confirm' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={addModalStep === 'confirm' ? 'bank-acct-confirm-title' : 'bank-acct-modal-title'}
            onClick={(ev) => ev.stopPropagation()}
          >
            {addModalStep === 'form' ? (
              <>
                <h2 id="bank-acct-modal-title" className="bank-acct-modal-title">
                  Add bank account
                </h2>
                <p className="bank-acct-modal-hint">
                  Enter your bank account details for verification. This information is used to confirm account ownership.
                </p>
                <form className="bank-acct-modal-form" onSubmit={runVerifyOnly}>
                  <input
                    className="bank-acct-modal-input"
                    placeholder="Account holder name"
                    value={verify.account_holder_name}
                    onChange={(e) => setVerify((p) => ({ ...p, account_holder_name: e.target.value }))}
                    required
                    autoComplete="name"
                  />
                  <input
                    className="bank-acct-modal-input"
                    placeholder="Account number"
                    inputMode="numeric"
                    value={verify.account_number}
                    onChange={(e) => setVerify((p) => ({ ...p, account_number: e.target.value }))}
                    required
                    autoComplete="off"
                  />
                  <input
                    className="bank-acct-modal-input"
                    placeholder="IFSC code"
                    value={verify.ifsc_code}
                    onChange={(e) => setVerify((p) => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))}
                    required
                    autoComplete="off"
                  />
                  {modalError ? <p className="bank-acct-modal-error">{modalError}</p> : null}
                  <div className="bank-acct-modal-actions">
                    <button type="button" className="bank-acct-modal-btn bank-acct-modal-btn--ghost" onClick={closeAddModal} disabled={submitting}>
                      Cancel
                    </button>
                    <button type="submit" className="bank-acct-modal-btn bank-acct-modal-btn--primary" disabled={submitting}>
                      {submitting ? 'Verifying…' : 'Verify'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 id="bank-acct-confirm-title" className="bank-acct-confirm-title">
                  Bank account verification successful!
                </h2>
                <div className="bank-acct-verified-box">
                  <p className="bank-acct-verified-box-heading">Verified Account Details</p>
                  <dl className="bank-acct-verified-rows">
                    <div className="bank-acct-verified-row">
                      <dt>Account Holder Name</dt>
                      <dd>{pendingLink?.bankVerifiedName || '—'}</dd>
                    </div>
                    <div className="bank-acct-verified-row">
                      <dt>Bank Name</dt>
                      <dd>{pendingLink?.bankName ? String(pendingLink.bankName).toUpperCase() : 'N/A'}</dd>
                    </div>
                    <div className="bank-acct-verified-row">
                      <dt>Account Number</dt>
                      <dd>{maskAccountNumber(verify.account_number)}</dd>
                    </div>
                    <div className="bank-acct-verified-row">
                      <dt>IFSC Code</dt>
                      <dd>{verify.ifsc_code || '—'}</dd>
                    </div>
                  </dl>
                </div>
                <p className="bank-acct-confirm-prompt">Do you want to add this verified bank account to your profile?</p>
                {modalError ? <p className="bank-acct-modal-error">{modalError}</p> : null}
                <div className="bank-acct-confirm-actions">
                  <button type="button" className="bank-acct-confirm-cancel" onClick={closeAddModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="button" className="bank-acct-confirm-add" onClick={confirmLink} disabled={submitting}>
                    {submitting ? 'Adding…' : 'Confirm & Add'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default BankAccountsPage;
